import {CommonModule, DatePipe} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {ChangeDetectorRef, Component, inject, OnInit, TemplateRef} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {firstValueFrom} from 'rxjs';
import {ConfirmDialogService} from '../../shared/commons/dialog/confirm-dialog/confirm-dialog.service';
import {ToastService} from '../../shared/commons/toast/toast-service';
import {UserCertificateDto, UserCertificateService} from './user-certificate.service';

@Component({
  selector: 'app-user-certificate-modal',
  standalone: true,
  imports: [CommonModule, DatePipe, FormsModule],
  templateUrl: './user-certificate-modal.component.html',
  styleUrl: './user-certificate-modal.component.scss'
})
export class UserCertificateModalComponent implements OnInit {
  protected readonly activeModal = inject(NgbActiveModal);
  private readonly modalService = inject(NgbModal);
  private readonly userCertificateService = inject(UserCertificateService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly toastService = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);

  certificate: UserCertificateDto | null = null;
  previewCertificate: UserCertificateDto | null = null;
  selectedFile: File | null = null;
  certificatePassword = '';
  loading = true;
  uploading = false;
  previewing = false;
  notFound = false;
  errorMessage = '';
  uploadErrorMessage = '';

  ngOnInit(): void {
    void this.loadCertificate();
  }

  private async loadCertificate(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';
    this.uploadErrorMessage = '';
    this.notFound = false;

    try {
      const certificate = await firstValueFrom(this.userCertificateService.getMyCertificate());
      this.applyCertificate(certificate);
    } catch (error) {
      const httpError = error as HttpErrorResponse;
      if (httpError.status === 404) {
        await this.ensureCertificate();
        return;
      }

      this.certificate = null;
      this.errorMessage = 'ไม่สามารถโหลดข้อมูลใบรับรองอิเล็กทรอนิกส์ได้';
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private async ensureCertificate(): Promise<void> {
    try {
      const certificate = await firstValueFrom(this.userCertificateService.ensureMyCertificate());
      this.applyCertificate(certificate);
    } catch {
      this.certificate = null;
      this.notFound = true;
      this.errorMessage = 'ไม่สามารถสร้างข้อมูลใบรับรองอิเล็กทรอนิกส์เริ่มต้นได้';
      this.cdr.detectChanges();
    }
  }

  private applyCertificate(certificate: UserCertificateDto | null | undefined): void {
    this.certificate = certificate ?? null;
    this.notFound = !this.certificate;
    if (this.certificate) {
      this.errorMessage = '';
    }
    this.cdr.detectChanges();
  }

  displayCertificate(): UserCertificateDto | null {
    return this.previewCertificate ?? this.certificate;
  }

  hasCertificateFile(): boolean {
    return !!this.certificate?.certificateFile;
  }

  protected getDownloadFileName(): string {
    return this.certificate?.certificateFileName || 'certificate.cer';
  }

  async downloadCertificateFile(): Promise<void> {
    if (!this.certificate) {
      return;
    }
    try {
      const blob = await firstValueFrom(this.userCertificateService.downloadMyCertificateFile());
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = this.getDownloadFileName();
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      this.toastService.error('ไม่สามารถดาวน์โหลดไฟล์ใบรับรองได้');
      this.cdr.detectChanges();
    }
  }

  getFileStatusBadge(): { label: string; variant: 'success' | 'danger' | 'warning' } {
    const cert = this.displayCertificate();
    if (!cert) {
      return {label: '-', variant: 'warning'};
    }
    if (cert.certificateFileExists) {
      return {label: 'พบไฟล์ในระบบ', variant: 'success'};
    }
    if (this.selectedFile) {
      return {label: 'ยังไม่บันทึกไฟล์', variant: 'warning'};
    }
    return {label: 'ไม่พบไฟล์ในระบบ', variant: 'danger'};
  }

  async onCertificateSelected(event: Event, passwordModal: TemplateRef<unknown>): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    this.selectedFile = file;
    this.certificatePassword = '';
    const confirmed = await this.modalService.open(passwordModal, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
    }).result.catch(() => false);
    if (!confirmed) {
      this.selectedFile = null;
      this.certificatePassword = '';
      input.value = '';
      return;
    }
    await this.previewSelectedCertificate();
  }

  protected submitCertificatePassword(modal: { close: (value?: any) => void }): void {
    modal.close(true);
  }

  private async previewSelectedCertificate(): Promise<void> {
    if (!this.selectedFile) {
      return;
    }

    this.uploadErrorMessage = '';
    this.previewCertificate = null;
    this.previewing = true;
    this.cdr.detectChanges();
    try {
      this.previewCertificate = await firstValueFrom(this.userCertificateService.previewMyCertificate(this.selectedFile, this.certificatePassword));
    } catch (error) {
      this.previewCertificate = null;
      const apiMessage = (error as HttpErrorResponse).error?.message;
      this.uploadErrorMessage = apiMessage || 'ไม่สามารถอ่านข้อมูลใบรับรองจากไฟล์ที่เลือกได้';
      this.toastService.error('อ่านข้อมูลใบรับรองจากไฟล์ไม่สำเร็จ');
    } finally {
      this.previewing = false;
      this.cdr.detectChanges();
    }
  }

  async saveCertificate(): Promise<void> {
    if (!this.selectedFile || this.uploading) {
      return;
    }

    const confirmed = await firstValueFrom(this.confirmDialogService.open({
      title: 'ยืนยันการบันทึก',
      content: `ยืนยันการอัปโหลดไฟล์ใบรับรอง "${this.selectedFile.name}" ใช่หรือไม่`,
      confirmButtonMsg: 'บันทึก',
      closeButtonMsg: 'ยกเลิก'
    }));
    if (!confirmed) {
      return;
    }

    this.uploading = true;
    this.uploadErrorMessage = '';
    this.cdr.detectChanges();

    try {
      const certificate = await firstValueFrom(this.userCertificateService.uploadMyCertificate(this.selectedFile, this.certificatePassword));
      this.applyCertificate(certificate);
      this.previewCertificate = certificate;
      this.selectedFile = null;
      this.certificatePassword = '';
      this.toastService.success('บันทึกข้อมูลใบรับรองอิเล็กทรอนิกส์สำเร็จ');
    } catch {
      this.uploadErrorMessage = 'ไม่สามารถอัปโหลดไฟล์ใบรับรองอิเล็กทรอนิกส์ได้';
      this.toastService.error('บันทึกข้อมูลใบรับรองอิเล็กทรอนิกส์ไม่สำเร็จ');
      this.cdr.detectChanges();
    } finally {
      this.uploading = false;
      this.cdr.detectChanges();
    }
  }
}
